<div class="ks-container">
  <a class="ks-back ks-rise-1" routerLink="/dashboard/catalogues/templates">
    <i class="bi bi-arrow-left" aria-hidden="true"></i>
    Volver a plantillas
  </a>

  <!-- Orden obligatorio: carga -> error de carga -> ficha -> no encontrada. El
       estado vacio solo puede aparecer cuando ya se sabe que no hay datos. -->
  @if (!loaded()) {
    <section class="ks-panel ks-rise-2">
      <div class="ks-skeleton-stack">
        <app-skeleton shape="block" height="140px" label="Cargando la plantilla" />
        <app-skeleton width="40%" height="18px" />
        <app-skeleton width="65%" />
      </div>
    </section>

    <section class="ks-panel ks-rise-3">
      <div class="ks-skeleton-stack">
        <app-skeleton width="30%" height="18px" label="Cargando los campos" />
        <div class="ks-fields-skeleton">
          <app-skeleton height="44px" [count]="5" />
        </div>
      </div>
    </section>
  } @else if (templateError()) {
    <section class="ks-panel ks-rise-2">
      <app-empty-state
        icon="wifi-off"
        title="No se pudo cargar la plantilla"
        [message]="templateError()!">
        <button type="button" class="ks-btn ks-btn--glass ks-btn--sm" (click)="reload()">
          <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
          Reintentar
        </button>
      </app-empty-state>
    </section>
  } @else if (template(); as template) {
    <section class="ks-panel ks-rise-2 ks-head" aria-labelledby="ks-template-name">
      <span class="ks-head__thumb" aria-hidden="true">
        @if (template.image) {
          <img [src]="template.image.urlFile" alt="" />
        } @else {
          <i class="bi bi-file-earmark-text"></i>
        }
      </span>

      <div class="ks-head__body">
        <h1 class="ks-page-title" id="ks-template-name">{{ template.name }}</h1>

        <div class="ks-head__meta">
          <span class="ks-chip ks-chip--purple ks-mono">{{ template.code }}</span>

          @if (template.generateQr) {
            <span class="ks-badge ks-badge--info">
              <i class="bi bi-qr-code" aria-hidden="true"></i>
              Genera QR
            </span>
          }

          @if (template.status) {
            <span class="ks-badge ks-badge--success">Activa</span>
          } @else {
            <span class="ks-badge ks-badge--muted">Inactiva</span>
          }
        </div>

        @if (template.description) {
          <p class="ks-page-subtitle">{{ template.description }}</p>
        }

        <dl class="ks-head__dates">
          <div>
            <dt>Creada</dt>
            <dd>{{ template.createdAt | dateToMoment }}</dd>
          </div>
          <div>
            <dt>Actualizada</dt>
            <dd>{{ template.updatedAt | dateToMoment }}</dd>
          </div>
        </dl>
      </div>

      <div class="ks-head__actions">
        <button type="button" class="ks-btn ks-btn--glass ks-btn--sm" (click)="edit()">
          <i class="bi bi-pencil" aria-hidden="true"></i>
          Editar
        </button>

        @if (template.status) {
          <button type="button" class="ks-btn ks-btn--red ks-btn--sm" (click)="deactivate()">
            <i class="bi bi-slash-circle" aria-hidden="true"></i>
            Desactivar
          </button>
        } @else {
          <button type="button" class="ks-btn ks-btn--green ks-btn--sm" (click)="activate()">
            <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
            Activar
          </button>
        }
      </div>
    </section>

    <section class="ks-panel ks-rise-3" aria-labelledby="ks-template-fields-heading">
      <div class="ks-card__header">
        <div>
          <h2 class="ks-card__title" id="ks-template-fields-heading">Campos</h2>
          <p class="ks-panel__hint">
            El orden manda: es el que la app usa al pintar el formulario. Arrastra una fila o usa
            las flechas y confirma los cambios. Los prerellenados (Netflix, Gmail…) se administran
            al editar cada campo.
          </p>
        </div>

        <button type="button" class="ks-btn ks-btn--primary ks-btn--sm" (click)="createField()">
          <i class="bi bi-plus-lg" aria-hidden="true"></i>
          Nuevo campo
        </button>
      </div>

      <!-- Anuncia SOLO el inicio de cada alta/edicion/borrado/reintento/descarte
           de un campo (ver `announce()` en el componente). El resultado final
           (exito o error) ya lo anuncia el `aria-live` propio de
           `<app-toasts>`; repetirlo aqui inundaria al lector de pantalla con
           el mismo aviso dos veces. -->
      <div class="ks-visually-hidden" aria-live="polite" aria-atomic="true">{{ liveMessage() }}</div>

      @if (orderChanged()) {
        <div class="ks-order-bar" role="status">
          <span class="ks-order-bar__text">
            <i class="bi bi-arrow-down-up" aria-hidden="true"></i>
            Cambiaste el orden de los campos. Todavía no se guarda.
          </span>

          <span class="ks-order-bar__actions">
            <button type="button" class="ks-btn ks-btn--ghost ks-btn--sm" (click)="cancelOrder()">
              Descartar
            </button>
            <button
              type="button"
              class="ks-btn ks-btn--green ks-btn--sm"
              [disabled]="hasBlockingFields()"
              [attr.aria-describedby]="hasBlockingFields() ? 'ks-order-block-hint' : null"
              (click)="saveOrder()">
              <i class="bi bi-check-lg" aria-hidden="true"></i>
              Guardar orden
            </button>
          </span>

          @if (hasBlockingFields()) {
            <p class="ks-panel__hint ks-order-bar__hint" id="ks-order-block-hint">
              <i class="bi bi-hourglass-split" aria-hidden="true"></i>
              Espera a que terminen los campos pendientes (o resuélvelos) para guardar el orden.
            </p>
          }
        </div>
      }

      <!-- EL BUG QUE SE ARREGLA AQUI: esta condicion era
           `@if (orderedFields().length === 0)` a secas, sin mirar ninguna senal
           de carga, asi que el primer render pintaba «Sin campos» con la
           peticion todavia en vuelo. Orden obligatorio: carga -> vacio -> tabla.
           El fallo de carga NO se contempla en este panel: los campos llegan
           DENTRO de la plantilla, asi que si el GET falla no se llega hasta
           aqui (lo atrapa `templateError()` arriba). -->
      @if (reloadingFields()) {
        <div class="ks-fields-skeleton">
          <app-skeleton height="44px" [count]="5" label="Cargando los campos" />
        </div>
      } @else if (orderedFields().length === 0) {
        <app-empty-state
          icon="list-ol"
          title="Sin campos"
          message="Esta plantilla todavía no define ningún campo. Agrega el primero para darle forma.">
          <button type="button" class="ks-btn ks-btn--glass ks-btn--sm" (click)="createField()">
            <i class="bi bi-plus-lg" aria-hidden="true"></i>
            Nuevo campo
          </button>
        </app-empty-state>
      } @else {
        <div class="ks-table-wrap">
          <table class="ks-table ks-table--compact">
            <caption class="ks-visually-hidden">
              Campos de la plantilla, en el orden en que los muestra la app.
            </caption>

            <thead>
              <tr>
                <th scope="col"><span class="ks-visually-hidden">Orden</span>#</th>
                <th scope="col">Etiqueta</th>
                <th scope="col">Tipo de campo</th>
                <th scope="col">Obligatorio</th>
                <th scope="col">Principal</th>
                <th scope="col">Estado</th>
                <th scope="col"><span class="ks-visually-hidden">Acciones</span></th>
              </tr>
            </thead>

            <tbody>
              @for (field of orderedFields(); track field.templateFieldId; let index = $index, count = $count) {
                @let state = rowState(field);

                <tr
                  [attr.draggable]="state ? 'false' : 'true'"
                  class="ks-row"
                  [class.ks-row--dragging]="draggingIndex() === index"
                  [class.ks-row--off]="!field.status && !state"
                  [class.ks-row--pending]="state && state.status !== 'error'"
                  [class.ks-row--sync-error]="state && state.status === 'error'"
                  (dragstart)="onDragStart(index, field)"
                  (dragover)="onDragOver($event)"
                  (drop)="onDrop($event, index)"
                  (dragend)="onDragEnd()">
                  <td class="ks-row__handle">
                    <div class="ks-row__handle-inner">
                      @if (state) {
                        <span class="ks-row__handle-status" aria-hidden="true">
                          <i class="bi {{ rowStatusIcon(state.status) }}"
                             [class.ks-spin]="state.status !== 'error'"></i>
                        </span>
                      } @else {
                        <span class="ks-grip" aria-hidden="true"><i class="bi bi-grip-vertical"></i></span>
                      }
                      <span class="ks-nums">{{ index + 1 }}</span>
                    </div>
                  </td>

                  <td>{{ field.label }}</td>

                  <td><span class="ks-chip ks-chip--purple ks-mono">{{ field.fieldType }}</span></td>

                  <td>
                    @if (field.required) {
                      <span class="ks-badge ks-badge--warning">Sí</span>
                    } @else {
                      <span class="ks-badge ks-badge--muted">No</span>
                    }
                  </td>

                  <td>
                    @if (field.primaryField) {
                      <span class="ks-badge ks-badge--info">
                        <i class="bi bi-star-fill" aria-hidden="true"></i>
                        Principal
                      </span>
                    }
                  </td>

                  <td>
                    @if (state) {
                      <span class="ks-sync-status" [class.ks-sync-status--error]="state.status === 'error'">
                        <i class="bi {{ rowStatusIcon(state.status) }}"
                           [class.ks-spin]="state.status !== 'error'"
                           aria-hidden="true"></i>
                        {{ rowStatusText(state.status) }}
                      </span>
                    } @else if (field.status) {
                      <span class="ks-badge ks-badge--success">Activo</span>
                    } @else {
                      <span class="ks-badge ks-badge--muted">Inactivo</span>
                    }
                  </td>

                  <td>
                    @if (state && state.status === 'error') {
                      <div class="ks-table__actions">
                        <button
                          type="button"
                          class="ks-btn ks-btn--ghost ks-btn--sm"
                          (click)="discardField(field)">
                          <i class="bi bi-trash3" aria-hidden="true"></i>
                          Descartar
                        </button>

                        <button
                          type="button"
                          class="ks-btn ks-btn--glass ks-btn--sm"
                          (click)="retryCreateField(field)">
                          <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
                          Reintentar
                        </button>
                      </div>
                    } @else {
                      <div class="ks-table__actions">
                        <button
                          type="button"
                          class="ks-icon-btn"
                          [disabled]="index === 0 || !!state"
                          [attr.aria-label]="rowActionLabel('Subir el campo ' + field.label, field)"
                          (click)="move(index, index - 1)">
                          <i class="bi bi-arrow-up" aria-hidden="true"></i>
                        </button>

                        <button
                          type="button"
                          class="ks-icon-btn"
                          [disabled]="index === count - 1 || !!state"
                          [attr.aria-label]="rowActionLabel('Bajar el campo ' + field.label, field)"
                          (click)="move(index, index + 1)">
                          <i class="bi bi-arrow-down" aria-hidden="true"></i>
                        </button>

                        <button
                          type="button"
                          class="ks-icon-btn"
                          [disabled]="!!state"
                          [attr.aria-label]="rowActionLabel('Editar el campo ' + field.label, field)"
                          (click)="editField(field)">
                          <i class="bi bi-pencil" aria-hidden="true"></i>
                        </button>

                        @if (field.status) {
                          <button
                            type="button"
                            class="ks-icon-btn ks-icon-btn--danger"
                            [disabled]="!!state"
                            [attr.aria-label]="rowActionLabel('Desactivar el campo ' + field.label, field)"
                            (click)="deactivateField(field)">
                            <i class="bi bi-slash-circle" aria-hidden="true"></i>
                          </button>
                        } @else {
                          <button
                            type="button"
                            class="ks-icon-btn"
                            [disabled]="!!state"
                            [attr.aria-label]="rowActionLabel('Activar el campo ' + field.label, field)"
                            (click)="activateField(field)">
                            <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
                          </button>
                        }
                      </div>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <p class="ks-panel__hint ks-panel__hint--foot" aria-live="polite">
          {{ activeFieldCount() }} de {{ orderedFields().length }} campos activos.
        </p>
      }
    </section>
  } @else {
    <section class="ks-panel ks-rise-2">
      <app-empty-state
        icon="exclamation-triangle"
        title="Plantilla no encontrada"
        message="La plantilla no existe o ya no está disponible.">
        <a class="ks-btn ks-btn--glass ks-btn--sm" routerLink="/dashboard/catalogues/templates">
          Volver a plantillas
        </a>
      </app-empty-state>
    </section>
  }
</div>
